import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Heart, Shield, LogOut, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e1ff2bf4-1540-494b-b229-04ddc96f1193/artifacts/g70ytwbu_1163.jpg";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Youth Victory", path: "/youth-victory" },
  { name: "Veterans", path: "/veterans" },
  { name: "Sleepover", path: "/sleepover" },
  { name: "Partners", path: "/partners" },
  { name: "Activism", path: "/activism" },
  { name: "Police Appreciation", path: "/police-appreciation" },
  { name: "Peace Pilot", path: "/peace-pilot" },
  { name: "Events", path: "/events" },
  { name: "Blog", path: "/blog" },
  { name: "Volunteer", path: "/volunteer" },
  { name: "Ways to Give", path: "/ways-to-give" },
  { name: "Contact", path: "/contact" },
];

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isAdmin, logout } = useAuth();

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
      {/* Admin Banner */}
      {isAdmin && (
        <div className="bg-[#0056D2] text-white text-center py-1 text-sm">
          <div className="flex items-center justify-center gap-2">
            <Shield className="w-4 h-4" />
            Admin Mode - You can edit gallery content
            <Link to="/admin/dashboard" className="ml-4 underline hover:no-underline flex items-center gap-1">
              <LayoutDashboard className="w-3 h-3" />
              Dashboard
            </Link>
            <button onClick={logout} className="ml-2 underline hover:no-underline flex items-center gap-1">
              <LogOut className="w-3 h-3" />
              Logout
            </button>
          </div>
        </div>
      )}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Peace for Change Alliance Inc" 
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm ${location.pathname === link.path ? "active" : ""}`}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Donate Button and Admin Link */}
          <div className="hidden lg:flex items-center gap-4">
            {!isAdmin && (
              <Link to="/admin/login" className="text-slate-500 hover:text-[#0056D2] text-sm flex items-center gap-1" data-testid="admin-login-link">
                <Shield className="w-4 h-4" />
                Admin
              </Link>
            )}
            <Link to="/donate">
              <Button 
                className="bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full px-6 py-2 font-semibold flex items-center gap-2"
                data-testid="donate-btn-nav"
              >
                <Heart className="w-4 h-4" />
                Donate
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-slate-700" />
            ) : (
              <Menu className="w-6 h-6 text-slate-700" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-100 animate-fadeIn">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 text-slate-700 font-medium rounded-lg ${
                    location.pathname === link.path ? "bg-blue-50 text-[#0056D2]" : ""
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              {!isAdmin && (
                <Link
                  to="/admin/login"
                  className="px-4 py-2 text-slate-500 font-medium rounded-lg flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Shield className="w-4 h-4" />
                  Admin Login
                </Link>
              )}
              <Link 
                to="/donate" 
                className="mx-4"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Button 
                  className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3 font-semibold flex items-center justify-center gap-2"
                  data-testid="mobile-donate-btn"
                >
                  <Heart className="w-4 h-4" />
                  Donate Now
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
